import * as Yup from 'yup';

import { PayoutType, ExpensesType } from 'types';

import { FormValues, GetInitialValues, ExpenseTypeCashStatus } from './types';

export const hideReceiptFieldTypes: ExpensesType[] = [
  ExpensesType.Misc,
  ExpensesType.Bonus,
  ExpensesType.Advance,
];

export const getInitialValues = ({
  eventId,
  employeeId,
  isOnlyCashLocation,
}: GetInitialValues): FormValues => ({
  eventId,
  date: '',
  notes: '',
  amount: '',
  categoryId: '',
  receipt: undefined,
  employeeId: employeeId || '',
  isCashFromEvent: isOnlyCashLocation || false,
  type: isOnlyCashLocation ? ExpensesType.Bonus : undefined,
});

const getRequiredValidationByType = ({
  message,
  withReceipt,
}: {
  message?: string;
  withReceipt?: boolean;
}) => ({
  then: (schema: Yup.MixedSchema | Yup.NumberSchema) =>
    schema.required(message),
  otherwise: (schema: Yup.MixedSchema | Yup.NumberSchema) =>
    schema.notRequired(),
  is: (type?: ExpensesType) =>
    type &&
    withReceipt &&
    !hideReceiptFieldTypes.includes(type as ExpensesType),
});

export const getValidationSchema = ({
  withReceipt,
  cashFlowBalance,
  showSelectExpensesType,
}: {
  withReceipt?: boolean;
  cashFlowBalance?: number;
  showSelectExpensesType?: boolean;
}) => {
  const typeValidation = showSelectExpensesType
    ? Yup.string().required()
    : Yup.string();

  const baseAmountValidation = Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '-' || originalValue === '') {
        return undefined;
      }
      return value;
    })
    .required('Amount is required')
    .test(
      'is-valid-number',
      'Amount must be a valid number',
      (value) => value !== undefined && !isNaN(value)
    );

  const amountValidation = baseAmountValidation.when('type', {
    is: (type: ExpensesType) => type === ExpensesType.Misc,
    then: (schema) =>
      schema.test(
        'misc-amount',
        'Amount cannot be zero',
        (value) => value !== 0
      ),
    otherwise: (schema) =>
      schema.positive('Amount must be positive').when('isCashFromEvent', {
        is: true,
        otherwise: (schema) => schema,
        then: (schema) =>
          typeof cashFlowBalance === 'number'
            ? schema.max(
                cashFlowBalance,
                `Amount cannot exceed cash balance (${cashFlowBalance})`
              )
            : schema,
      }),
  });

  return Yup.object().shape({
    type: typeValidation,
    amount: amountValidation,
    date: Yup.date().required(),
    isCashFromEvent: Yup.boolean(),
    eventId: Yup.number().required(),
    employeeId: Yup.number().required(),
    notes: Yup.string().min(5).max(200).required(),
    categoryId: Yup.number().when(
      'type',
      getRequiredValidationByType({ withReceipt })
    ),
    receipt: Yup.mixed().when(
      'type',
      getRequiredValidationByType({
        withReceipt,
        message: 'File is a required field',
      })
    ),
  });
};

export const expenseTypeToPayoutMapping: Record<
  ExpensesType,
  Record<ExpenseTypeCashStatus, PayoutType>
> = {
  [ExpensesType.Misc]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Misc,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.Misc,
  },
  [ExpensesType.Bonus]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Bonus,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidBonus,
  },
  [ExpensesType.Advance]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Advance,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidAdvance,
  },
  [ExpensesType.Expense]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Expense,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidExpense,
  },
  [ExpensesType.Reimbursement]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Reimbursements,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidReimbursements,
  },
};
